/**
 * Avatar - a person's photo, or their initials in a circle when they have none.
 *
 * A closed account gets a neutral grey placeholder rather than initials,
 * because "Du" for "Deleted user" would look like a real person.
 */
import { UserX } from 'lucide-react';

import { initials, photoUrl, isDeleted } from '../utils/format';
import { useI18n } from '../hooks/useI18n';

export default function Avatar({ person, size = 'md' }) {
  const { t } = useI18n();
  if (!person) return null;

  const sizeClass = size === 'lg' ? ' avatar-lg' : size === 'sm' ? ' avatar-sm' : '';
  const name = `${person.firstName || ''} ${person.lastName || ''}`.trim();

  // ---- Closed account ----
  if (isDeleted(person)) {
    return (
      <span
        className={`avatar-initials${sizeClass}`}
        style={{ background: 'var(--color-border)', color: 'var(--color-text-disabled)' }}
        title={t('misc.accountClosed')}
        aria-label={t('misc.deletedUser')}
      >
        <UserX size={size === 'lg' ? 32 : size === 'sm' ? 16 : 20} aria-hidden="true" />
      </span>
    );
  }

  const photo = photoUrl(person.profilePicture);

  if (photo) {
    return <img className={`avatar${sizeClass}`} src={photo} alt={name} loading="lazy" />;
  }

  return (
    <span className={`avatar-initials${sizeClass}`} aria-hidden="true">
      {initials(person.firstName, person.lastName)}
    </span>
  );
}
